import { useState, useEffect, useMemo } from 'react';
import { MessageSquare, Send, BellRing } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ordenesAPI } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export function TecnicoMensajesCard({ orden, mensajes, onRefresh, onMensajeAdd }) {
  const { user } = useAuth();
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [marcandoLeidos, setMarcandoLeidos] = useState(false);

  // Estado local de mensajes para actualización inmediata
  const [localMensajes, setLocalMensajes] = useState(mensajes || []);

  // Sincronizar cuando cambian los mensajes desde el padre
  useEffect(() => {
    setLocalMensajes(mensajes || []);
  }, [mensajes]);

  // Cuántos mensajes están sin leer para el técnico actual
  const emailActual = (user?.email || '').toLowerCase();
  const noLeidos = useMemo(() => {
    if (!emailActual) return 0;
    return localMensajes.filter(m => {
      if ((m.autor || '').toLowerCase() === emailActual) return false;
      const readBy = (m.read_by || []).map(e => (e || '').toLowerCase());
      return !readBy.includes(emailActual);
    }).length;
  }, [localMensajes, emailActual]);

  const handleMarcarLeidos = async () => {
    if (!orden?.id || noLeidos === 0) return;
    setMarcandoLeidos(true);
    try {
      await api.post(`/ordenes/${orden.id}/mensajes/marcar-leidos`);
      const refreshed = await ordenesAPI.obtenerMensajes(orden.id);
      setLocalMensajes(refreshed.data || []);
      if (onRefresh) onRefresh();
    } catch {
      toast.error('No se pudo marcar como leídos');
    } finally {
      setMarcandoLeidos(false);
    }
  };

  const handleEnviarMensaje = async () => {
    if (!nuevoMensaje.trim()) return;
    
    setEnviandoMensaje(true);
    try {
      const response = await ordenesAPI.añadirMensaje(orden.id, {
        mensaje: nuevoMensaje.trim(),
        visible_tecnico: true
      });
      
      // Extraer el objeto mensaje de la respuesta
      // El backend devuelve: { message: "...", mensaje: {...} }
      const nuevoMsgObj = response?.data?.mensaje || {
        mensaje: nuevoMensaje.trim(),
        rol: 'tecnico',
        autor_nombre: 'Técnico',
        fecha: new Date().toISOString(),
        visible_tecnico: true
      };
      
      // Actualizar estado local
      setLocalMensajes(prev => [...prev, nuevoMsgObj]);
      if (onMensajeAdd) onMensajeAdd(nuevoMsgObj);
      
      setNuevoMensaje('');
      toast.success('Mensaje enviado');
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setEnviandoMensaje(false);
    }
  };

  return (
    <Card className={noLeidos > 0 ? 'border-red-300 ring-2 ring-red-200' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Comunicaciones
            {noLeidos > 0 && (
              <Badge
                className="bg-red-600 text-white hover:bg-red-600 animate-pulse"
                data-testid="tecnico-badge-no-leidos"
              >
                <BellRing className="w-3 h-3 mr-1" />
                {noLeidos} nuevo{noLeidos === 1 ? '' : 's'}
              </Badge>
            )}
          </CardTitle>
          {noLeidos > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarcarLeidos}
              disabled={marcandoLeidos}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="tecnico-marcar-leidos"
            >
              {marcandoLeidos ? 'Marcando...' : 'Marcar como leídos'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
          {localMensajes.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No hay mensajes en esta orden
            </p>
          ) : (
            localMensajes.map((msg, index) => {
              const readBy = (msg.read_by || []).map(e => (e || '').toLowerCase());
              const esMio = (msg.autor || '').toLowerCase() === emailActual;
              const sinLeer = !esMio && !readBy.includes(emailActual);
              return (
                <div
                  key={msg.id || index}
                  className={`p-3 rounded-lg ${
                    msg.rol === 'admin'
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : 'bg-muted/50 border-l-4 border-l-slate-400'
                  } ${sinLeer ? 'ring-2 ring-red-300 shadow-md' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{msg.autor_nombre}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {msg.rol === 'admin' ? 'Admin' : 'Técnico'}
                    </Badge>
                    {sinLeer && (
                      <Badge className="text-[10px] bg-red-600 text-white hover:bg-red-600">
                        NUEVO
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(msg.fecha).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <p className="text-sm">{msg.mensaje}</p>
                </div>
              );
            })
          )}
        </div>

        <Separator className="my-4" />
        <div className="flex gap-2">
          <Textarea
            placeholder="Escribe un mensaje..."
            value={nuevoMensaje}
            onChange={(e) => setNuevoMensaje(e.target.value)}
            rows={2}
            className="flex-1"
          />
          <Button 
            onClick={handleEnviarMensaje}
            disabled={enviandoMensaje || !nuevoMensaje.trim()}
            className="self-end"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
